import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import useToggle from 'hooks/useToggle'
import { take, EmptyArray } from 'lib/array/arrayUtils'
import { isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import FlashHotelDesktopRateOption from './FlashHotelDesktopRateOption'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileDetails from 'components/AccommodationKit/AccommodationRoomTileDetails'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import AccommodationRoomTileActions from 'components/AccommodationKit/AccommodationRoomTileActions'
import { getAccommodationRoomTileActionsState } from 'components/AccommodationKit/getAccommodationRoomTileActionsState'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { useAppSelector } from 'hooks/reduxHooks'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import { getDynamicPricingUrgencyLabelData } from '../utils'
import { useInView } from 'react-intersection-observer'
import config from 'constants/config'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { useUrgentOfferRoomDateData } from 'hooks/Offers/useUrgentOfferRoomDateData'
import Group from 'components/utils/Group'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import TextButton from 'LuxKit/Button/TextButton'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Sheet from 'components/utils/Sheet'
import ContactContext from 'contexts/contactContext'
import GeoContext from 'contexts/geoContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import ModalContext from 'contexts/ModalContext'

const RateSection = styled.div`
  > * {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const ShowMoreContent = styled(SmallToggleButton)`
  padding: ${rem(12)};
  width: 100%;
  justify-content: center;
`

interface Props {
  offer: App.HotelOffer
  roomTypeId: string
  packageViews: Array<App.OfferPackageOptionView>
  rooms: Array<OfferPageRoom>
  previewMode: boolean
  checkIn?: string
  availableRoomCount?: number
  availableForPurchase?: boolean
  canRedeemLuxPlusBenefits: boolean
  suggestedPackageOption?: {
    uniqueKey: string
    message: string
  }
}

const initialRatesToShow = 4

function FlashHotelDesktopSameRoomOption(props: Props) {
  const {
    offer,
    roomTypeId,
    packageViews,
    rooms,
    previewMode,
    checkIn,
    availableRoomCount,
    availableForPurchase,
    canRedeemLuxPlusBenefits,
    suggestedPackageOption,
  } = props

  const offerId = offer.id
  const isMultiRoom = rooms.length > 1
  const roomCount = rooms.length

  const cachedRoomDateData = useUrgentOfferRoomDateData(offerId, roomTypeId)

  const pkg = packageViews[0].package

  const liveRoomsBeforeSurcharge = useMemo(() => getDynamicPricingUrgencyLabelData(cachedRoomDateData, packageViews[0].availableRooms), [cachedRoomDateData, packageViews])
  const urgencyTagAbTestVariation = useAppSelector(state => getOptimizelyExperimentVariation(state, OptimizelyExperiments.lereDynamicPricingUrgencyTag))
  const [sectionRef, sectionInView] = useInView({ triggerOnce: false, threshold: 0.4 })
  const canTriggerExperiment = useMemo(() => {
    return sectionInView && !!liveRoomsBeforeSurcharge && config.BRAND === 'luxuryescapes'
  }, [sectionInView, liveRoomsBeforeSurcharge])
  useOptimizelyExperiment(OptimizelyExperiments.lereDynamicPricingUrgencyTag, canTriggerExperiment)
  const contactContext = useContext(ContactContext)
  const { currentRegionCode } = useContext(GeoContext)
  const showModal = useContext(ModalContext)
  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.hotelsAndVillasRequestAgentCallbackEnabled)

  const [experimentHideRoomsRemainingLabel, experimentRoomsBeforeSurcharge] = useMemo(() => {
    if (urgencyTagAbTestVariation === 'on') {
      return [!!liveRoomsBeforeSurcharge, liveRoomsBeforeSurcharge]
    }
    return [false, undefined]
  }, [urgencyTagAbTestVariation, liveRoomsBeforeSurcharge])

  const [showingMoreRates, showMoreRates] = useToggle()

  // Availability calculations
  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable),
  ], [packageViews])

  const isAllRoomsAvailable = packageViews.every(option => option.availableRooms && option.availableRooms >= roomCount)
  const isAllRoomsHaveAvailabilityData = packageViews.every(option => typeof option.availableRooms === 'number')
  // Use explicit === 0 check to distinguish "sold out" (0) from "loading" (undefined)
  const isSoldOut = !!checkIn && packageViews.every(option => option.availableRooms === 0)
  const hasExceededCapacity = packageViews.every(option => option.hotelAvailable && !option.capacityValid)
  const hasHotelMemberPrice = packageViews.some(option => option.hotelMemberPrice > 0)

  const shouldPreventMultipleRoomsBooking = roomCount > 2 && canRedeemLuxPlusBenefits && hasHotelMemberPrice

  const availablePackageViews = useMemo(() => packageViews.filter(view => view.capacityValid && view.hotelAvailable), [packageViews])
  /**
   * Preview is used only by content people.
   * We don't want to show unavailable rateOptions to the user.
   */
  const rateOptionViews = previewMode ? packageViews : availablePackageViews

  // Compute the state for AccommodationRoomTileActions
  // Don't show error messages when phone booking is required
  const actionsState = useMemo(() => {
    if (shouldPreventMultipleRoomsBooking) {
      return undefined
    }
    return getAccommodationRoomTileActionsState({
      availableForPurchase,
      roomFitsCapacity,
      datesAvailable,
      isMultiRoom,
      checkIn,
      isSoldOut,
      hasExceededCapacity,
      isAllRoomsAvailable,
      isAllRoomsHaveAvailabilityData,
    })
  }, [
    availableForPurchase,
    checkIn,
    datesAvailable,
    hasExceededCapacity,
    isAllRoomsAvailable,
    isAllRoomsHaveAvailabilityData,
    isMultiRoom,
    isSoldOut,
    roomFitsCapacity,
    shouldPreventMultipleRoomsBooking,
  ])

  const offerPageDispatch = useOfferPageDispatch()

  const toggleFilter = useCallback((filter: OfferPageFilterType) => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, pkg.roomType])

  const handleChangeDatesClick = useCallback(() => {
    toggleFilter('checkin')
  }, [toggleFilter])

  const onChangeOccupancyClick = useCallback(() => {
    toggleFilter('capacity')
  }, [toggleFilter])

  const onMultiRoomModeClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleMultiRoomMode, multiRoomMode: 'individual' })
  }, [offerPageDispatch])

  const onCallbackButtonClick = useCallback(() => {
    showModal(<RequestAgentCallbackModal type="HOTELS_AND_VILLAS" />)
  }, [showModal])

  const croPopularOptionBookingRemaining = !!useOptimizelyExperiment(OptimizelyExperiments.croPopularOptionBookingRemaining, !!availableRoomCount && !!checkIn)

  if (!pkg.roomType) { return null }

  // Phone booking banner for special case
  const phoneBookingBanner = shouldPreventMultipleRoomsBooking ? (
    <Sheet background="neutral-seven" padding={20}>
      <Group direction="vertical" gap={8}>
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold">Ready to book? Call us to complete your booking</BodyText>
          <BodyText variant="medium">
            Our team are available {NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) ? '7 days a week from 9am to 7pm' : '24 hours, 7 days'}
          </BodyText>
        </Group>
        <TextButton startIcon={<LinePhoneIcon />} kind="primary" href={`tel:${contactContext.defaultContact.number}`} fit="flex">
          Call {contactContext.defaultContact.humanReadable}
        </TextButton>
        {callbacksFeatureEnabled && (
          <TextButton kind="secondary" fit="flex" onClick={onCallbackButtonClick}>
            Request a callback
          </TextButton>
        )}
      </Group>
    </Sheet>
  ) : null

  return (
    <Pane ref={sectionRef} border="neutral-five" borderRadius="S" data-testid="package-option">
      <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
      <AccommodationRoomTile roomPackage={pkg}>
        <AccommodationRoomTileImage
          roomPackage={pkg}
        />
        <AccommodationRoomTileDetails
          roomPackage={pkg}
          urgencyLabels={<>
            {!datesAvailable && <UrgencyLabel variant="critical2">'SOLD OUT'</UrgencyLabel>}
            <BookingsCountLabel bookings={pkg.roomType.stats.booked} threshold="room" />
            {checkIn && !croPopularOptionBookingRemaining && !experimentHideRoomsRemainingLabel && <RoomsRemainingLabel roomCount={packageViews[0].availableRooms ?? 0} threshold="rate" />}
            {checkIn && croPopularOptionBookingRemaining && !experimentHideRoomsRemainingLabel && availableRoomCount && <OfferUrgencyRoomCount availableRoomCount={availableRoomCount} /> }
          </>}
          actions={availableForPurchase && datesAvailable && roomFitsCapacity && <CSSBreakpoint min="desktop">
            <TripGuard>
              <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} />
            </TripGuard>
          </CSSBreakpoint>}
          roomCount={rooms.length}
        >
          <AccommodationRoomTileActions
            roomPackage={pkg}
            state={actionsState}
            onChangeOccupancyClick={onChangeOccupancyClick}
            onChangeDatesClick={handleChangeDatesClick}
            onMultiRoomModeClick={onMultiRoomModeClick}
          />
          {phoneBookingBanner}
        </AccommodationRoomTileDetails>
      </AccommodationRoomTile>
      <RateSection>
        {take(rateOptionViews, showingMoreRates ? 100 : initialRatesToShow).map((view, index) => <FlashHotelDesktopRateOption
          key={view.package.uniqueKey}
          roomsBeforeSurcharge={experimentRoomsBeforeSurcharge}
          offer={offer}
          optionNumber={index + 1}
          packageView={view}
          showOptionLabel={rateOptionViews.length > 1}
          suggestedPackageMessage={suggestedPackageOption?.uniqueKey === view.package.uniqueKey ? suggestedPackageOption.message : undefined}
        />)}
        {rateOptionViews.length > initialRatesToShow &&
          <ShowMoreContent
            on={showingMoreRates}
            onClick={showMoreRates}
            size="small"
          >
            {showingMoreRates ? 'Show less options' : 'Show more options'}
          </ShowMoreContent>}
      </RateSection>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms ?? EmptyArray,
  checkIn: state.checkInDate,
  previewMode: appState.system.previewMode,
  availableForPurchase: isAvailableForPurchase(state, appState),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
  open: !!state.activeFilter,
}), FlashHotelDesktopSameRoomOption, preventRenderWithOpenFilter)
